/**
 * CriarRegraDaOrdem — "Criar regra a partir desta ordem" no cartão do resultado
 * da limpeza (spec 2026-09-30 §6.1, TASK-12120).
 *
 * A regra só nasce de um caso real: ordem concluída, remedição que baixou o uso,
 * e pedida por quem está olhando. As três condições são do servidor
 * (`rotas_service.criar` confere de novo todas, e mais os portões do verbo);
 * aqui elas só decidem se o botão aparece, para ninguém clicar num botão que só
 * pode falhar.
 *
 * O host é o do ALERTA (`remedicao.alerta_host`), nunca o Name tag da máquina: é
 * ele que a regra compara com os próximos alertas. Ordem antiga não o guardou —
 * aí o botão não aparece, porque o servidor recusaria (`host_do_alerta_desconhecido`).
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Anchor, Button, Group, Modal, NumberInput, Stack, Text, TextInput } from '@mantine/core'
import { IconAlertTriangle, IconRepeat } from '@tabler/icons-react'

import { criarRegra, fraseDaRecusa, padraoCobreOHost } from '@/api/regrasDeOrdem'
import { FONTE_DADO } from './Dado'

export interface CriarRegraDaOrdemProps {
  tenantId: number
  planId: string
  /** Host do alerta de onde a ordem nasceu; vazio = ordem antiga, sem botão. */
  host: string
  antes: number
  depois: number
  /** Decidido pelo servidor (`e_o_solicitante` do estado do plano). */
  eOSolicitante: boolean
  /** `resultado.status` da ordem: só `concluida` vale. */
  status: string | null | undefined
  /** O caso que a regra copia (§6.1), só leitura: a montagem e o limiar do alerta
   * (`resultado.remedicao`) e o verbo do plano. */
  montagem: string
  limiarPct: number
  verbo: string
}

const TETO = { min: 1, max: 14, padrao: 3 }

function formatarPct(v: number): string {
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}

function LinhaDoCaso({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <Text size="sm">
      <Text span size="sm" c="dimmed">{rotulo}: </Text>
      <Text span size="sm" style={{ fontFamily: FONTE_DADO }}>{valor}</Text>
    </Text>
  )
}
const VALIDADE = { min: 1, max: 90, padrao: 90 }

function dentro(v: number, f: { min: number; max: number }): boolean {
  return Number.isInteger(v) && v >= f.min && v <= f.max
}

export function CriarRegraDaOrdem({
  tenantId, planId, host, antes, depois, eOSolicitante, status, montagem, limiarPct, verbo,
}: CriarRegraDaOrdemProps) {
  const [aberto, setAberto] = useState(false)
  const [hostPadrao, setHostPadrao] = useState(host)
  const [teto, setTeto] = useState<number>(TETO.padrao)
  const [validade, setValidade] = useState<number>(VALIDADE.padrao)
  const [enviando, setEnviando] = useState(false)
  const [recusa, setRecusa] = useState<string | null>(null)
  const [criada, setCriada] = useState<number | null>(null)

  if (status !== 'concluida' || !(depois < antes) || !eOSolicitante || !host) return null

  const rotaDaLista = `/tenants/${tenantId}/regras-de-ordem`
  if (criada != null) {
    return (
      <Text size="xs" mb="sm">
        Regra{' '}
        <Text span size="xs" fw={600} style={{ fontFamily: FONTE_DADO }}>#{criada}</Text>
        {' '}criada.{' '}
        <Anchor size="xs" component={Link} to={rotaDaLista}>Ver as regras de ordem</Anchor>
      </Text>
    )
  }

  const padraoOk = padraoCobreOHost(hostPadrao.trim(), host)
  const podeEnviar = padraoOk && dentro(teto, TETO) && dentro(validade, VALIDADE)

  const abrir = () => {
    setHostPadrao(host)
    setTeto(TETO.padrao)
    setValidade(VALIDADE.padrao)
    setRecusa(null)
    setAberto(true)
  }

  const enviar = async () => {
    setRecusa(null)
    setEnviando(true)
    try {
      const regra = await criarRegra(tenantId, {
        origem_plan_id: planId,
        host_padrao: hostPadrao.trim(),
        teto_semana: teto,
        valida_dias: validade,
      })
      setAberto(false)
      setCriada(regra.id)
    } catch (erro) {
      setRecusa(fraseDaRecusa(erro, 'criar'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <Group gap="xs" mb="sm">
        <Button
          size="xs"
          variant="light"
          color="inovareTeal"
          leftSection={<IconRepeat size={14} />}
          onClick={abrir}
        >
          Criar regra a partir desta ordem
        </Button>
      </Group>

      <Modal opened={aberto} onClose={() => setAberto(false)} title="Criar regra a partir desta ordem">
        <Stack gap="sm">
          <Text size="sm">
            Quando o mesmo alerta de disco voltar, o @DevOps prepara o laudo e avisa você. A
            limpeza continua sendo escolhida por você e, em produção, aprovada por outro
            integrante do time antes de executar.
          </Text>
          {/* O que a regra copia da ordem e não se muda aqui: mudar é revogar e criar
              outra a partir de uma ordem nova (§6.3). */}
          <Stack gap={2} data-escopo-do-caso>
            <LinhaDoCaso rotulo="Montagem" valor={montagem} />
            <LinhaDoCaso rotulo="Limiar do alerta" valor={`≥ ${formatarPct(limiarPct)}`} />
            <LinhaDoCaso rotulo="Limpeza" valor={verbo} />
          </Stack>
          <TextInput
            label="Hosts que a regra cobre"
            description={
              <>
                O host do alerta,{' '}
                <Text span size="xs" style={{ fontFamily: FONTE_DADO }}>{host}</Text>
                , ou um começo dele terminado em *.
              </>
            }
            value={hostPadrao}
            onChange={(e) => setHostPadrao(e.currentTarget.value)}
            error={padraoOk ? null : 'Use o próprio host ou um começo dele terminado em *.'}
            styles={{ input: { fontFamily: FONTE_DADO } }}
          />
          <NumberInput
            label="Execuções por semana, no máximo"
            description="De 1 a 14."
            min={TETO.min}
            max={TETO.max}
            clampBehavior="none"
            allowDecimal={false}
            value={teto}
            error={dentro(teto, TETO) ? null : 'O teto vai de 1 a 14.'}
            onChange={(v) => setTeto(typeof v === 'number' ? v : Number(v))}
            styles={{ input: { fontFamily: FONTE_DADO } }}
          />
          <NumberInput
            label="Validade, em dias"
            description="No máximo 90. Depois disso a regra para de valer."
            min={VALIDADE.min}
            max={VALIDADE.max}
            clampBehavior="none"
            allowDecimal={false}
            value={validade}
            error={dentro(validade, VALIDADE) ? null : 'A validade vai de 1 a 90 dias.'}
            onChange={(v) => setValidade(typeof v === 'number' ? v : Number(v))}
            styles={{ input: { fontFamily: FONTE_DADO } }}
          />
          {recusa && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">{recusa}</Text>
            </Alert>
          )}
          <Group gap="xs" justify="flex-start">
            <Button color="inovareTeal" disabled={!podeEnviar} loading={enviando} onClick={enviar}>
              Criar regra
            </Button>
            <Button variant="default" onClick={() => setAberto(false)}>Cancelar</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
